import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AudioPlayer from './AudioPlayer';
import { useLectura } from '../context/LecturaContext';
import { validarCodigoPromo } from '../utils/api';

function Resultado() {
  const navigate = useNavigate();
  const { resultado, limpiarResultado, desbloquear } = useLectura();
  const [textoVisible, setTextoVisible]   = useState(0);
  const [codigoInput, setCodigoInput]     = useState('');
  const [validando, setValidando]         = useState(false);
  const [errorCodigo, setErrorCodigo]     = useState('');
  const [revelando, setRevelando]         = useState(false);

  useEffect(() => {
    if (!resultado) { navigate('/'); return; }
    // Revelar secciones visibles una a una
    let t = 0;
    const avanzar = () => {
      t++;
      setTextoVisible(t);
      if (t < 3) setTimeout(avanzar, 1600);
    };
    const inicio = setTimeout(avanzar, 800);
    return () => clearTimeout(inicio);
  }, [resultado, navigate]);

  // Fix React 18 batching: desbloquear DESPUÉS de que AnimatePresence
  // haya podido ejecutar la exit animation de "revelando"
  useEffect(() => {
    if (!revelando) return;
    const t = setTimeout(() => {
      // Primero ocultamos el velo (sale con animación)
      setRevelando(false);
      // Luego en el siguiente frame activamos el contenido premium
      requestAnimationFrame(() => {
        setTimeout(() => desbloquear(), 50);
      });
    }, 1800);
    return () => clearTimeout(t);
  }, [revelando, desbloquear]);

  if (!resultado) return null;

  const { nombre, tipoMano, intencion, mano, esPremium, lectura, audios } = resultado;

  const iconosIntencion  = { amor: '❤️', salud: '💚', trabajo: '💼' };
  const nombresIntencion = { amor: 'Amor y relaciones', salud: 'Salud y bienestar', trabajo: 'Trabajo y éxito' };
  const nombresMano      = { tierra: '🌍 Mano de Tierra', agua: '💧 Mano de Agua', aire: '💨 Mano de Aire', fuego: '🔥 Mano de Fuego' };

  const handleDesbloquear = async (e) => {
    e.preventDefault();
    if (!codigoInput.trim()) return;
    setValidando(true);
    setErrorCodigo('');
    try {
      await validarCodigoPromo(codigoInput.trim().toUpperCase());
      // El useEffect de revelando se encarga de desbloquear con timing correcto
      setRevelando(true);
    } catch (err) {
      setErrorCodigo(err.response?.data?.error || 'Código no válido. Inténtalo de nuevo.');
    } finally {
      setValidando(false);
    }
  };

  return (
    <div className="contenedor" style={{ paddingBottom: '4rem' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>

        {/* Audio Intro */}
        {audios?.intro && <AudioPlayer src={audios.intro} autoPlay={true} />}

        {/* Cabecera */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}
            style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {iconosIntencion[intencion]}
          </motion.div>
          <h1 className="titulo" style={{ fontSize: '2rem' }}>Tu lectura, {nombre}</h1>
          <p style={{ color: 'var(--color-texto-suave)' }}>
            {nombresIntencion[intencion]} • {nombresMano[tipoMano]}
            {mano && <span style={{ marginLeft: '0.5rem' }}>• {mano === 'diestra' ? '✍️ Mano activa' : '🤚 Mano del alma'}</span>}
          </p>
          {esPremium && (
            <span style={{ background: 'var(--color-oro)', color: 'var(--color-fondo)', padding: '0.3rem 1rem',
              borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold', marginTop: '0.5rem', display: 'inline-block' }}>
              ✨ REVELACIÓN COMPLETA
            </span>
          )}
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto' }}>

          {/* SECCIÓN 1 — Intro */}
          {textoVisible >= 1 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
              style={{ background: 'var(--color-secundario)', padding: '1.5rem', borderRadius: '12px',
                marginBottom: '1.5rem', borderLeft: '4px solid var(--color-oro)' }}>
              <p style={{ fontSize: '1.1rem', fontStyle: 'italic', color: 'var(--color-oro)' }}>
                “{lectura.intro}”
              </p>
            </motion.div>
          )}

          {/* SECCIÓN 2 — Mano */}
          {textoVisible >= 2 && lectura.manoTexto && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
              style={{ background: 'rgba(255,215,0,0.06)', padding: '1.5rem', borderRadius: '12px',
                marginBottom: '1.5rem', border: '1px solid rgba(255,215,0,0.25)' }}>
              <h3 style={{ color: 'var(--color-oro)', marginBottom: '0.5rem' }}>
                {mano === 'diestra' ? '✍️ Tu mano activa habla…' : '🤚 Tu mano del alma revela…'}
              </h3>
              <p style={{ fontStyle: 'italic' }}>{lectura.manoTexto}</p>
            </motion.div>
          )}

          {/* SECCIÓN 3 — General */}
          {textoVisible >= 3 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
              style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--color-acento)', marginBottom: '0.5rem' }}>Lo que el Maestro Solis ve</h3>
              <p>{lectura.general}</p>
            </motion.div>
          )}

          {/* ────────────────── BARRERA — PREMIUM ────────────────── */}
          <AnimatePresence mode="wait">
          {textoVisible >= 3 && !esPremium && !revelando && (
            <motion.div key="bloqueado" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.6, delay: 0.4 }}>

              {/* Secciones borrosas */}
              {[{
                titulo: `En tu ${nombresIntencion[intencion].toLowerCase()}`,
                texto: lectura.especifica
              }, {
                titulo: '🌙 El consejo que no puede callar',
                texto: lectura.consejo
              }].map((s, i) => (
                <div key={i} style={{ position: 'relative', marginBottom: '1.5rem', borderRadius: '12px', overflow: 'hidden' }}>
                  {/* Contenido borroso */}
                  <div style={{ filter: 'blur(6px)', userSelect: 'none', pointerEvents: 'none',
                    padding: '1.5rem', background: i === 1 ? 'rgba(233,69,96,0.08)' : 'var(--color-secundario)',
                    border: i === 1 ? '1px solid var(--color-acento)' : 'none', borderRadius: '12px' }}>
                    <h3 style={{ color: i === 1 ? 'var(--color-oro)' : 'var(--color-acento)', marginBottom: '0.5rem' }}>{s.titulo}</h3>
                    <p>{s.texto}</p>
                  </div>
                  {/* Overlay con 🔒 */}
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: 'rgba(10,10,20,0.45)', borderRadius: '12px' }}>
                    <span style={{ fontSize: '1.6rem' }}>🔮</span>
                  </div>
                </div>
              ))}

              {/* Frase de intriga del Maestro */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                style={{ textAlign: 'center', margin: '1.5rem 0 0.5rem', padding: '0 1rem' }}>
                <p style={{ color: 'var(--color-oro)', fontStyle: 'italic', fontSize: '1rem', lineHeight: 1.6 }}>
                  “Hay algo más en esta mano, <strong>{nombre}</strong>.<br />
                  Algo que el Maestro Solis no puede ignorar.”
                </p>
              </motion.div>

              {/* Fragmento tentador borroso */}
              <div style={{ margin: '1rem 0', padding: '1rem 1.5rem',
                background: 'linear-gradient(135deg, rgba(255,215,0,0.05), rgba(233,69,96,0.05))',
                border: '1px dashed rgba(255,215,0,0.3)', borderRadius: '10px',
                filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none', fontSize: '0.9rem',
                color: 'var(--color-texto-suave)', fontStyle: 'italic' }}>
                “…tu línea del corazón muestra una interrupción que…”<br />
                “…hay una contradicción entre tus dos manos que…”
              </div>

              {/* CTA — input de código */}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
                style={{ marginTop: '1.5rem', padding: '1.8rem',
                  background: 'linear-gradient(135deg, rgba(255,215,0,0.07), rgba(233,69,96,0.07))',
                  borderRadius: '16px', border: '1px solid rgba(255,215,0,0.4)', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔮</div>
                <h3 style={{ color: 'var(--color-oro)', marginBottom: '0.5rem' }}>La Revelación Completa</h3>
                <p style={{ fontSize: '0.88rem', color: 'var(--color-texto-suave)', marginBottom: '1.2rem', lineHeight: 1.6 }}>
                  El Maestro Solis guardó tu lectura completa.<br />
                  Si tienes un código de acceso, úsalo ahora.
                </p>
                <form onSubmit={handleDesbloquear} style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={codigoInput}
                    onChange={e => { setCodigoInput(e.target.value.toUpperCase()); setErrorCodigo(''); }}
                    placeholder="CÓDIGO DE ACCESO"
                    maxLength={20}
                    style={{ flex: 1, minWidth: '160px', maxWidth: '220px', padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,215,0,0.4)',
                      borderRadius: '8px', color: 'var(--color-texto)', fontSize: '0.95rem',
                      textAlign: 'center', letterSpacing: '2px', outline: 'none' }}
                  />
                  <button type="submit" className="boton" disabled={validando}
                    style={{ padding: '0.75rem 1.4rem', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                    {validando ? '🔮 Verificando…' : 'Revelar'}
                  </button>
                </form>
                {errorCodigo && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    style={{ color: 'var(--color-acento)', marginTop: '0.7rem', fontSize: '0.85rem' }}>
                    {errorCodigo}
                  </motion.p>
                )}
              </motion.div>
            </motion.div>
          )}

          {/* Animación de revelación */}
          {revelando && (
            <motion.div key="revelando" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔮</div>
              <p style={{ color: 'var(--color-oro)', fontStyle: 'italic', fontSize: '1.1rem' }}>
                El Maestro Solis levanta el velo…
              </p>
            </motion.div>
          )}

          {/* Secciones premium visibles al desbloquear */}
          {esPremium && (
            <motion.div key="desbloqueado" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
                style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ color: 'var(--color-acento)', marginBottom: '0.5rem' }}>
                  En tu {nombresIntencion[intencion].toLowerCase()}
                </h3>
                <p>{lectura.especifica}</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                style={{ background: 'rgba(233,69,96,0.1)', padding: '1.5rem', borderRadius: '12px',
                  marginBottom: '2rem', border: '1px solid var(--color-acento)' }}>
                <h3 style={{ color: 'var(--color-oro)', marginBottom: '0.5rem' }}>🌙 El consejo del Maestro Solis</h3>
                <p style={{ fontWeight: '500' }}>{lectura.consejo}</p>
              </motion.div>

              {/* Outro */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }}
                style={{ textAlign: 'center', marginTop: '2rem' }}>
                <div style={{ fontSize: '1.15rem', color: 'var(--color-oro)', fontStyle: 'italic',
                  padding: '2rem', borderTop: '1px solid rgba(255,215,0,0.3)', borderBottom: '1px solid rgba(255,215,0,0.3)',
                  lineHeight: 1.7 }}>
                  “{lectura.outro}”
                </div>
                {audios?.outro && <div style={{ marginTop: '1.5rem' }}><AudioPlayer src={audios.outro} autoPlay={false} /></div>}
              </motion.div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* Botones finales — siempre visibles una vez cargado */}
          {textoVisible >= 3 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
              style={{ marginTop: '2.5rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="boton" onClick={() => { limpiarResultado(); navigate('/lectura'); }}>Nueva lectura</button>
              <button className="boton"
                onClick={() => {
                  const texto = `${lectura.textoCompleto}\n\n— El Maestro Solis`;
                  if (navigator.share) { navigator.share({ title: 'Mi lectura de mano', text: texto }); }
                  else { navigator.clipboard.writeText(texto); alert('Texto copiado al portapapeles'); }
                }}
                style={{ background: 'var(--color-secundario)' }}>Compartir</button>
            </motion.div>
          )}

        </div>
      </motion.div>
    </div>
  );
}

export default Resultado;